import { camelToSentenceCase } from '@go-shared/lib/string';
import { SimpleTemplate } from '@go-shared/types/template';
import { LinkFragment, TemplateFragmentType } from '@go-shared/types/templateFragment';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import React, { useEffect, useMemo, useState } from 'react';
import uuid from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  BubbleButton,
  BubbleInput,
  BubbleSelect,
  BubbleTable,
  BubbleOverlay,
  BubbleAlert,
} from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { getDayGroupConflicts } from '@go-client/lib/api/templateFragment';
import { Property } from '@go-shared/types/property';
import { getDayFriendlyName } from '@go-shared/lib/sailing';
import { FormGenericContainer } from '../FormGenericContainer';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';

interface TemplateMappingPageProps {
  title?: string;
  property: Property;
  sailingBundles: BareSimpleSailingDayBundle[];
  templates: SimpleTemplate[];
  selectedTemplates: SimpleTemplate[];
  onGoBack?: () => void;
  onClose?: () => void;
  onFinishLinkTemplateFragments?: (linkFragments: LinkFragment[]) => Promise<void>;
}

interface TemplateMappingPageTable {
  key: string;
  bundle: BareSimpleSailingDayBundle;
  tableItems: TemplateMappingPageTableItem[];
}

interface TemplateMappingPageTableItem {
  key: string;
  checked: boolean;
  fragment: TemplateFragmentType;
  template: SimpleTemplate;
  replacesTemplate?: SimpleTemplate;
  _source: BareSimpleSailingDayBundle;
}

export function TemplateMappingPage(props: TemplateMappingPageProps) {
  const [tables, setTables] = useState<TemplateMappingPageTable[]>([]);
  const [dayGroupConflicts, setDayGroupConflicts] = useState<string[] | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    const newTables = props.sailingBundles.map(bndl => {
      const { templateFragments } = bndl.day;

      const tableItems: TemplateMappingPageTableItem[] = [
        {
          key: 'item1',
          checked: false,
          fragment: 'events',
          template: props.selectedTemplates[0],
          replacesTemplate:
            templateFragments && templateFragments.events
              ? props.templates.find(t => t._id === templateFragments.events)
              : undefined,
          _source: bndl,
        },
        {
          key: 'item2',
          checked: false,
          fragment: 'venueData',
          template: props.selectedTemplates[0],
          replacesTemplate:
            templateFragments && templateFragments.venueData
              ? props.templates.find(t => t._id === templateFragments.venueData)
              : undefined,
          _source: bndl,
        },
        {
          key: 'item3',
          checked: false,
          fragment: 'content',
          template: props.selectedTemplates[0],
          replacesTemplate:
            templateFragments && templateFragments.content
              ? props.templates.find(t => t._id === templateFragments.content)
              : undefined,
          _source: bndl,
        },
        {
          key: 'item4',
          checked: false,
          fragment: 'feed',
          template: props.selectedTemplates[0],
          replacesTemplate:
            templateFragments && templateFragments.feed
              ? props.templates.find(t => t._id === templateFragments.feed)
              : undefined,
          _source: bndl,
        },
      ];

      return {
        key: uuid(),
        bundle: bndl,
        tableItems,
      };
    });

    setTables(newTables);
  }, [props]);

  useEffect(() => {
    if (!tables.length) return;

    fetchGroupConflicts().catch(console.error);
  }, [tables]);

  const linkFragments = useMemo(() => {
    let lfs: LinkFragment[] = [];

    tables.forEach(table => {
      table.tableItems.forEach(item => {
        if (!item.checked) return;

        lfs.push({
          templateId: item.template._id,
          fragmentType: item.fragment,
          sailingId: table.bundle.sailing._id,
          sailingDayId: table.bundle.day._id,
        });
      });
    });

    return lfs;
  }, [tables]);

  async function fetchGroupConflicts() {
    const allPossibleLinkFragments = tables
      .map(t =>
        t.tableItems.map(ti => ({
          sailingId: t.bundle.sailing._id,
          sailingDayId: t.bundle.day._id,
          templateId: ti.template._id,
          fragmentType: ti.fragment,
        }))
      )
      .flat();

    const dgc = await getDayGroupConflicts(props.property._id, allPossibleLinkFragments);

    setDayGroupConflicts(dgc);
  }

  function handleSelectAll(checked: boolean, tableKey?: string) {
    const newTables = [...tables];

    const tIndx = tables.findIndex(table => table.key === tableKey);

    if (tIndx < 0) return;

    const newTableItems = newTables[tIndx].tableItems.map(ti => ({ ...ti, checked }));

    newTables.splice(tIndx, 1, {
      ...newTables[tIndx],
      tableItems: newTableItems,
    });

    setTables(newTables);
  }

  const tableFields: Array<BubbleTableFields<TemplateMappingPageTableItem>> = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      render: (items, tableKey) => {
        let allSelected = true;

        if (items) {
          items.forEach(itm => (allSelected = allSelected && itm.checked));
        }

        return (
          <BubbleInput
            variant="inverse"
            inputClassName="stretched-link"
            type="checkbox"
            checked={allSelected}
            onValue={(checked: boolean) => handleSelectAll(checked, tableKey)}
          />
        );
      },
    },
    { key: 'fragment' },
    { key: 'template', behavior: BubbleTableFieldBehavior.Short },
    {
      key: 'replaces',
      label: 'unlinks',
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<TemplateMappingPageTableItem>> = [
    {
      key: 'select',
      render: (data, index) => (
        <BubbleInput
          type="checkbox"
          inputClassName="stretched-link"
          checked={data.checked}
          onValue={checked => {
            const newTables = [...tables];

            const tIndx = tables.findIndex(table => table.bundle.day._id === data._source.day._id);

            if (tIndx < 0) return;

            const newTableItems = [...newTables[tIndx].tableItems];

            newTableItems.splice(index, 1, {
              ...data,
              checked,
            });

            newTables.splice(tIndx, 1, {
              ...newTables[tIndx],
              tableItems: newTableItems,
            });

            setTables(newTables);
          }}
        />
      ),
    },
    {
      key: 'fragment',
      render: ({ checked, fragment }) => (
        <span className={!checked ? 'text-slate-dark' : ''}>
          {fragment === 'feed' ? 'Print Program' : camelToSentenceCase(fragment)}
        </span>
      ),
    },
    {
      key: 'template',
      render: (data, index) => (
        <BubbleSelect
          allowUndefined={false}
          value={data.template}
          disabled={!data.checked}
          options={props.selectedTemplates}
          keyExtractor={t => t._id}
          labelExtractor={t => t.name}
          internalValueExtractor={t => t._id}
          onValue={val => {
            const newTables = [...tables];

            const tIndx = tables.findIndex(table => table.bundle.day._id === data._source.day._id);

            if (tIndx < 0) return;

            const newTableItems = [...newTables[tIndx].tableItems];

            newTableItems.splice(index, 1, {
              ...data,
              template: val,
            });

            newTables.splice(tIndx, 1, {
              ...newTables[tIndx],
              tableItems: newTableItems,
            });

            setTables(newTables);
          }}
        />
      ),
    },
    {
      key: 'replaces',
      render: ({ checked, replacesTemplate }) =>
        replacesTemplate ? (
          <span
            className={
              !checked ? 'text-slate-dark font-semibold' : 'text-crimson-dark font-semibold'
            }
          >
            {replacesTemplate.name}
          </span>
        ) : (
          <span className="text-smoke italic">Not linked</span>
        ),
    },
  ];

  async function handleOnFinishLinkTemplateFragments() {
    if (!props.onFinishLinkTemplateFragments) return;

    setIsBusy(true);

    try {
      await props.onFinishLinkTemplateFragments(linkFragments);
    } finally {
      setIsBusy(false);
    }
  }

  function handleToggleFragment(fragment: TemplateFragmentType, checked: boolean) {
    const newTables = [...tables].map(t => ({
      ...t,
      tableItems: [...t.tableItems].map(ti => {
        if (ti.fragment !== fragment || ti._source.day.locked) return ti;

        return { ...ti, checked };
      }),
    }));

    setTables(newTables);
  }

  function handleSetFragmentToTemplate(fragment: TemplateFragmentType, index: number) {
    const newTables = [...tables].map(t => ({
      ...t,
      tableItems: [...t.tableItems].map(ti => {
        if (ti.fragment !== fragment || ti._source.day.locked) return ti;

        return {
          ...ti,
          checked: true,
          template: props.selectedTemplates[index],
        };
      }),
    }));

    setTables(newTables);
  }

  function handleMoveFragmentTemplate(fragment: TemplateFragmentType, moveAmt: number) {
    const newTables = [...tables].map(t => ({
      ...t,
      tableItems: [...t.tableItems].map(ti => {
        if (ti.fragment !== fragment || ti._source.day.locked || ti._source.day.locked) return ti;

        const currentIndx = props.selectedTemplates.findIndex(st => st._id === ti.template._id);

        let nextIndx = currentIndx + moveAmt;
        if (nextIndx < 0) nextIndx = props.selectedTemplates.length - 1;
        if (nextIndx > props.selectedTemplates.length - 1) nextIndx = 0;

        return {
          ...ti,
          checked: true,
          template: props.selectedTemplates[nextIndx],
        };
      }),
    }));

    setTables(newTables);
  }

  function handleAlternateFragmentTemplate(fragment: TemplateFragmentType) {
    let nextIndx = -1;

    const newTables = [...tables].map(t => ({
      ...t,
      tableItems: [...t.tableItems].map(ti => {
        if (ti.fragment !== fragment || ti._source.day.locked) return ti;

        nextIndx += 1;
        if (nextIndx > props.selectedTemplates.length - 1) nextIndx = 0;

        return {
          ...ti,
          checked: true,
          template: props.selectedTemplates[nextIndx],
        };
      }),
    }));

    setTables(newTables);
  }

  const hideQuickActions = tables.length <= 1;
  const dropdownItems = [
    { key: 'toggleOn', element: 'Check all' },
    { key: 'toggleOff', element: 'Uncheck all' },
    { key: 'setToFirst', element: 'Set all to first' },
    { key: 'setToLast', element: 'Set all to last' },
    { key: 'increment', element: 'Increment all by one' },
    { key: 'decrement', element: 'Decrement all by one' },
    { key: 'roundRobin', element: 'Alternate all' },
  ];

  async function dropdownOnSelect(item: DropdownItem, type: TemplateFragmentType) {
    switch (item.key) {
      case 'toggleOn':
        handleToggleFragment(type, true);
        break;
      case 'toggleOff':
        handleToggleFragment(type, false);
        break;
      case 'setToFirst':
        handleSetFragmentToTemplate(type, 0);
        break;
      case 'setToLast':
        handleSetFragmentToTemplate(type, props.selectedTemplates.length - 1);
        break;
      case 'increment':
        handleMoveFragmentTemplate(type, 1);
        break;
      case 'decrement':
        handleMoveFragmentTemplate(type, -1);
        break;
      case 'roundRobin':
        handleAlternateFragmentTemplate(type);
        break;
    }
  }

  return (
    <div className="tflf-5">
      <FormGenericContainer
        footerClassName="flex-wrap"
        footer={() => (
          <>
            <div className="text-slate-dark">
              <div>
                Templates Selected:
                <span className="font-semibold"> {props.selectedTemplates.length}</span>
              </div>
            </div>
            <BubbleButton
              className="ml-auto"
              variant="navy"
              inverse
              onClick={props.onGoBack}
              disabled={isBusy}
            >
              Go Back
            </BubbleButton>
            <BubbleOverlay loading={isBusy}>
              <BubbleButton
                variant="navy"
                onClick={handleOnFinishLinkTemplateFragments}
                disabled={linkFragments.length < 1}
              >
                Finish: {props.title || 'Link Template Fragments'}*
              </BubbleButton>
            </BubbleOverlay>
            <div className="text-right text-slate italic basis-full mt-1">
              *This operation may take a long time (up to a minute)
            </div>
          </>
        )}
      >
        <div className="flex items-start mb-3">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">
              {props.title || 'Link Template Fragments'}
            </h1>
            <h2 className="text-2xl text-navy">2. Map Fragments To Day(s)</h2>
          </div>
          <BubbleButton
            disabled={isBusy}
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        {!hideQuickActions && (
          <div className="-mx-8 -mt-3 px-8 py-5 sticky top-0 z-20 form-blur">
            <h3 className="mb-2 text-slate-darkest">Quick Actions</h3>
            <div className="dropdown-inline-fix mr-2">
              <Dropdown items={dropdownItems} onSelect={itm => dropdownOnSelect(itm, 'events')}>
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="lime"
                    icon="bolt-lightning"
                  >
                    Events
                  </BubbleButton>
                )}
              </Dropdown>
            </div>
            <div className="dropdown-inline-fix mr-2">
              <Dropdown items={dropdownItems} onSelect={itm => dropdownOnSelect(itm, 'venueData')}>
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="banana"
                    icon="bolt-lightning"
                  >
                    Venue Data
                  </BubbleButton>
                )}
              </Dropdown>
            </div>
            <div className="dropdown-inline-fix mr-2">
              <Dropdown items={dropdownItems} onSelect={itm => dropdownOnSelect(itm, 'content')}>
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="ocean"
                    icon="bolt-lightning"
                  >
                    Content
                  </BubbleButton>
                )}
              </Dropdown>
            </div>
            <div className="dropdown-inline-fix mr-2">
              <Dropdown items={dropdownItems} onSelect={itm => dropdownOnSelect(itm, 'feed')}>
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="grape"
                    icon="bolt-lightning"
                  >
                    Print Program
                  </BubbleButton>
                )}
              </Dropdown>
            </div>
          </div>
        )}
        <div className="-mx-8 px-8 py-3">
          <div className="-mb-12">
            {tables.map(table => (
              <div className="mb-12" key={table.key}>
                <h3 className="text-lg mb-3 text-slate-darkest">
                  {table.bundle.day.locked && (
                    <FontAwesomeIcon icon="lock" className="mr-2 text-smoke" />
                  )}
                  {getDayFriendlyName(table.bundle.sailing, table.bundle.day)}
                </h3>
                <BubbleOverlay
                  loading={table.bundle.day.locked}
                  replaceIcon={() => (
                    <BubbleAlert className="mb-3" variant="crimson">
                      <span className="font-semibold">This day is locked.</span> You cannot link
                      template fragments to locked days.
                    </BubbleAlert>
                  )}
                >
                  {dayGroupConflicts &&
                    dayGroupConflicts.includes(table.bundle.day._id) &&
                    table.tableItems[0].checked && (
                      <BubbleAlert variant="banana" className="mb-3 -mt-1">
                        <FontAwesomeIcon icon="triangle-exclamation" className="mr-2" />
                        Group events on this day cause conflicts with the selected template
                      </BubbleAlert>
                    )}
                  <BubbleTable
                    tableId={table.key}
                    headVariant="navy"
                    striped
                    responsive
                    fields={tableFields}
                    items={table.tableItems}
                    templates={tableTemplates}
                  />
                </BubbleOverlay>
              </div>
            ))}
          </div>
        </div>
      </FormGenericContainer>
    </div>
  );
}
